import React, { useState, useEffect } from "react";
import styled from "styled-components";
import axios from "axios";

const ShareLinksContainer = styled.div`
  margin-top: 20px;
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const ShareLinksTitle = styled.h3`
  margin-top: 0;
  margin-bottom: 15px;
  color: #333;
`;

// Renommer ce composant styled pour éviter le conflit
const LinksListContainer = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
`;

const ShareLinkItem = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #eee;

  &:last-child {
    border-bottom: none;
  }
`;

const ShareLinkInfo = styled.div`
  flex: 1;
`;

const ShareLinkUrl = styled.div`
  font-size: 14px;
  color: #1a73e8;
  margin-bottom: 5px;
  word-break: break-all;
`;

const ShareLinkMeta = styled.div`
  font-size: 12px;
  color: #666;
  display: flex;
  gap: 15px;
`;

const ShareLinkActions = styled.div`
  display: flex;
  gap: 10px;
`;

const ActionButton = styled.button`
  background-color: ${(props) => (props.danger ? "#f44336" : "#f5f5f5")};
  color: ${(props) => (props.danger ? "white" : "#333")};
  border: none;
  border-radius: 4px;
  padding: 6px 10px;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    background-color: ${(props) => (props.danger ? "#d32f2f" : "#e0e0e0")};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 20px;
  color: #666;
`;

function ShareLinksList({ reportId }) {
  const [shareLinks, setShareLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiUrl =
    process.env.REACT_APP_API_URL || "https://report-campaign.sirdata.io";

  useEffect(() => {
    const fetchShareLinks = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem("token");

        if (!token) {
          setError("Vous devez être connecté pour voir les liens de partage");
          setLoading(false);
          return;
        }

        const response = await axios.get(
          `${apiUrl}/api/share/links/${reportId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data.success) {
          setShareLinks(response.data.shareLinks);
        } else {
          setError("Erreur lors de la récupération des liens de partage");
        }
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des liens de partage:",
          error
        );
        setError(
          error.response?.data?.message ||
            "Erreur lors de la récupération des liens de partage"
        );
      } finally {
        setLoading(false);
      }
    };

    if (reportId) {
      fetchShareLinks();
    }
  }, [reportId, apiUrl]);
  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(url);
    // Optionnel: ajouter un feedback visuel
    alert("Lien copié dans le presse-papiers !");
  };

  const deleteShareLink = async (token) => {
    if (
      !window.confirm("Êtes-vous sûr de vouloir supprimer ce lien de partage ?")
    ) {
      return;
    }

    try {
      const jwtToken = localStorage.getItem("token");

      const response = await axios.delete(
        `${apiUrl}/api/share/links/${token}`,
        {
          headers: {
            Authorization: `Bearer ${jwtToken}`,
          },
        }
      );

      if (response.data.success) {
        // Mettre à jour la liste des liens
        setShareLinks(shareLinks.filter((link) => link.token !== token));
      } else {
        setError("Erreur lors de la suppression du lien de partage");
      }
    } catch (error) {
      console.error("Erreur lors de la suppression du lien de partage:", error);
      setError(
        error.response?.data?.message ||
          "Erreur lors de la suppression du lien de partage"
      );
    }
  };
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <ShareLinksContainer>
        Chargement des liens de partage...
      </ShareLinksContainer>
    );
  }

  if (error) {
    return <ShareLinksContainer>Erreur: {error}</ShareLinksContainer>;
  }

  return (
    <ShareLinksContainer>
      <ShareLinksTitle>Liens de partage ({shareLinks.length})</ShareLinksTitle>

      {shareLinks.length === 0 ? (
        <EmptyState>
          Aucun lien de partage n'a été créé pour ce rapport.
        </EmptyState>
      ) : (
        <LinksListContainer>
          {shareLinks.map((link) => (
            <ShareLinkItem key={link.token}>
              <ShareLinkInfo>
                <ShareLinkUrl>{link.shareUrl}</ShareLinkUrl>
                <ShareLinkMeta>
                  <span>Créé le: {formatDate(link.createdAt)}</span>
                  <span>Expire le: {formatDate(link.expiresAt)}</span>
                  {link.lastAccessed && (
                    <span>Dernier accès: {formatDate(link.lastAccessed)}</span>
                  )}
                  <span>Nombre d'accès: {link.accessCount}</span>
                </ShareLinkMeta>
              </ShareLinkInfo>
              <ShareLinkActions>
                <ActionButton onClick={() => copyToClipboard(link.shareUrl)}>
                  Copier
                </ActionButton>
                <ActionButton
                  danger
                  onClick={() => deleteShareLink(link.token)}
                >
                  Supprimer
                </ActionButton>
              </ShareLinkActions>
            </ShareLinkItem>
          ))}
        </LinksListContainer>
      )}
    </ShareLinksContainer>
  );
}

export default ShareLinksList;
